<template>
  <modal class="style-modal" @close-modal="closeModal">
    <spinner v-if="loading" />
    <div v-else-if="!createOfferMode">
      <h5 class="my-0 mb-2 header-info text-center">
        <b> EDIT ORDER - </b
        >{{ dayjs(props.fullGame.start).format('DD MMM hh:mm A') }} -
        {{ props.fullGame.participants[0].longName }} at
        {{ props.fullGame.participants[1].longName }}
        <h5
          v-if="fullGame.participants[0].mainPitcher"
          style="display: inline"
        >
          ({{ fullGame.participants[0].mainPitcher }})
        </h5>
      </h5>
      <h1 class="mt-2 mb-0 header-info text-center">
        <span class="justify-content-center">
          <div
            v-if="fullGame.league && fullGame.league.includes('PROPS')"
            class="propStyle"
          >
            {{ formatEventNameForDisplay(fullGame.eventName) }}
          </div>
          {{ getOrderSideString(props.fullGame, props.session) }}
          <div class="d-inline">
            <odds-selector
              :odds="orderOdds"
              class="d-inline-flex flex-grow-1"
              type="text"
              @update-odds="updateOdds"
            />
          </div>
          <informational-tooltip
            v-if="userStore.user.viewOddsWithCommission"
            class="text-lowercase font-weight-bold"
            :text-content="`The left value are your odds, and the odds below reflect what others will see if they use the view odds with commission setting`"
          />
        </span>
      </h1>
      <alert-notification
        v-if="
          !fullGame.live &&
          Number(expiry) !== initialExpiry &&
          Number(expiry) > endExpiry
        "
        type="warning"
        class="mt-2 mb-3"
      >
        <font-awesome-icon :icon="faExclamationTriangle" class="mr-2" />
        <span class="font-regular"
          >This order will expire when event starts!</span
        >
      </alert-notification>
      <div
        v-if="canOfferAsArb"
        class="arb-card"
        :class="{ 'is-on': arbOfferEnabled }"
        role="switch"
        :aria-checked="arbOfferEnabled"
        tabindex="0"
        @click="toggleArbOffer"
        @keydown.space.prevent="toggleArbOffer"
        @keydown.enter.prevent="toggleArbOffer"
      >
        <div class="arb-card-message">
          <font-awesome-icon
            :icon="arbOfferEnabled ? faBolt : faExclamationTriangle"
            class="arb-card-status-icon"
          />
          <span v-if="arbOfferEnabled" class="arb-card-text">
            Arb Offer on &mdash; you stay a maker and avoid taker fees.
          </span>
          <span v-else class="arb-card-text">
            {{ arbOrderMessage }}
          </span>
        </div>
        <div class="arb-card-controls">
          <div class="arb-card-title">
            <span class="arb-card-icon">
              <font-awesome-icon :icon="faBolt" />
            </span>
            <span class="arb-card-label">Arb Offer</span>
          </div>
          <a
            class="arb-card-learn"
            href="#"
            @click.stop.prevent="openArbOfferInfo"
          >Learn more</a>
          <span class="arb-card-spacer"></span>
          <span class="arb-card-state">{{ arbOfferEnabled ? 'On' : 'Off' }}</span>
          <span class="toggle-switch" aria-hidden="true">
            <span
              class="toggle-slider"
              :class="{ 'is-on': arbOfferEnabled }"
            ></span>
          </span>
        </div>
      </div>
      <div
        v-if="
          fullGame.hasOwnProperty('awayMoneylines') ||
          fullGame.hasOwnProperty('over')
        "
        class="flex-nowrap align-items-center mt-3"
      >
        <bid-ask-ladder
          :key="'bidaskladder'"
          :full-game="fullGame"
          :side="side"
          :edit-offer-mode="true"
          :your-side-name="getSideString(fullGame, session, false)"
          :spread-number="session.spread"
          :total-number="session.total"
          :other-side-name="getOtherSideString(fullGame, session, false)"
          :take-bet-mode="false"
          :risk="orderRisk"
          :bid-ladder="askLadder"
          :ask-ladder="bidLadder"
          :offer-is-in-arb="isInArb && !arbOfferEnabled"
          @open-dropdown="openDropdown"
        />
      </div>
      <div class="row risk-win-items mt-3">
        <div class="col-sm-4 col-12 align-items-center pr-2">
          <div class="input-group d-flex align-items-center">
            <input
              :key="session.odds"
              v-two-decimal
              inputmode="decimal"
              :value="orderRisk"
              maxlength="8"
              :placeholder="''"
              class="font-weight-bold d-inline-flex"
              type="text"
              @input="handleOrderRiskUpdate($event.target.value)"
              @blur="onFocusValues.risk = false"
            />
            <div class="input-group-div" @click="dropdownRisk = !dropdownRisk">
              Risk
              <font-awesome-icon
                :icon="dropdownRisk ? faAngleUp : faAngleDown"
                role="button"
                class="ml-2"
              />
              <div v-if="dropdownRisk" class="dropdown-menu">
                <div
                  v-for="item in riskWinOptions"
                  :key="item.concat('RISK')"
                  @click="handleOrderRiskDropdown(item)"
                >
                  {{ item }}
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="col-sm-4 col-12 align-items-center pr-2 pl-2">
          <div class="input-group d-flex align-items-center">
            <input
              v-two-decimal
              inputmode="decimal"
              :value="orderWin"
              maxlength="8"
              :placeholder="''"
              class="font-weight-bold d-inline-flex"
              type="text"
              @input="handleOrderWinUpdate($event.target.value)"
              @blur="onFocusValues.win = false"
            />
            <div class="input-group-div" @click="dropdownWin = !dropdownWin">
              To Win
              <font-awesome-icon
                :icon="dropdownWin ? faAngleUp : faAngleDown"
                role="button"
                class="ml-2"
              />
              <div v-if="dropdownWin" class="dropdown-menu">
                <div
                  v-for="item in riskWinOptions"
                  :key="item.concat('WIN')"
                  @click="handleOrderWinDropdown(item)"
                >
                  {{ item }}
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="col-sm-4 col-12 align-items-center pl-2">
          <div class="input-group d-flex align-items-center">
            <input
              :key="'expiryInput'"
              v-two-decimal
              inputmode="decimal"
              :value="expiry"
              :placeholder="expiryPending ? '…' : ''"
              :disabled="expiryPending"
              class="font-weight-bold d-inline-flex"
              type="text"
              @input="handleOrderExpiryUpdate($event.target.value)"
              @blur="onFocusValues.expiry = false"
            />
            <!-- placeholder="minutes" -->
            <div
              class="input-group-div"
              @click="expiryPending ? null : (dropdownExpiry = !dropdownExpiry)"
            >
              Expiry
              <font-awesome-icon
                :icon="dropdownExpiry ? faAngleUp : faAngleDown"
                role="button"
                class="ml-2"
              />
              <div v-if="dropdownExpiry" class="dropdown-menu expiry">
                <div
                  v-for="(item, i) in expiryItems"
                  :key="i"
                  @click="handleOrderExpiryDropdown(item)"
                >
                  {{ item.text }}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <alert-notification
        v-if="orderRisk > session.bet + available"
        type="error"
        class="mt-4"
      >
        <font-awesome-icon :icon="faExclamationTriangle" class="mr-1" />
        <span class="font-regular"
          >Risk > Available Balance (<a
            class="add-balance"
            @click="handleSetRiskToMaxAvailable"
            >{{ Math.floor(available) }}</a
          >)</span
        >
      </alert-notification>
      <alert-notification
        v-if="orderRisk > userStore.user.maxLiability"
        type="primary"
        :class="{
          'mt-3':
            orderRisk > userStore.user.maxLiability && orderRisk > available,
          'mt-4': orderRisk < available,
        }"
      >
        <font-awesome-icon
          :icon="faCircleExclamation"
          class="mr-1"
          color="#343b86"
        />
        <span class="mr-1">Max Risk = ${{ userStore.user.maxLiability }}.</span>
        <span v-if="parseFloat(orderRisk) <= 100000" class="font-regular">
          <a class="add-available-risk" @click="updateMaxRisk(parseFloat(orderRisk))"
            >Update to ${{ orderRisk }}</a
          >
        </span>
        <span v-else class="font-regular">
          Maximum allowed is $100,000
        </span>
      </alert-notification>
      <div class="row mt-4 pt-1">
        <div class="col-md-4 col-xs-12 mb-3">
          <button
            :disabled="
              loading ||
              expiryPending ||
              editOrderButtonDisabled ||
              !parseFloat(orderRisk) ||
              parseFloat(orderRisk) < 1 ||
              !parseFloat(orderWin) ||
              Math.abs(orderOdds) < 100 ||
              orderWin < 10 ||
              (userStore.user.oddsFormat === 'decimal' &&
                oddsComputed < 1.01) ||
              !validateZero ||
              available < 1 ||
              updatingMaxRisk ||
              (dontAllowArbs && isInArb && !arbOfferEnabled)
            "
            :class="['edit-order-btn', { 'btn-max-balance': exceedsBalanceButCanEditMax }]"
            @click="exceedsBalanceButCanEditMax ? editOrderAtMax() : submitEditOrder()"
          >
            <font-awesome-icon :icon="faPen" role="button" class="mr-1" />
            <span v-if="userStore.user.oddsFormat !== 'decimal'">
              <b> Edit Order {{ exceedsBalanceButCanEditMax ? '(Max)' : '' }} </b>
              <br />
              {{ exceedsBalanceButCanEditMax ? `${formatOddsNoCommission(orderOdds)} | $${maxRiskForEdit.toFixed(2)}` : editOrderButtonString }}
            </span>
            <span v-else>
              <b> Edit Order {{ exceedsBalanceButCanEditMax ? '(Max)' : '' }} </b>
              <br />
              {{ oddsComputed }} | {{ exceedsBalanceButCanEditMax ? maxRiskForEdit.toFixed(2) : orderRisk }}
            </span>
          </button>
        </div>
        <div class="col-md-4 col-xs-12 mb-3">
          <button class="cancel-button" @click="cancelOrder">
            <font-awesome-icon :icon="faTrashAlt" role="button" class="mr-1" />
            <b> Cancel Order </b>
          </button>
        </div>
        <div class="col-md-4 col-xs-12 mb-3">
          <button class="create-offer-btn" @click="handleOfferOtherSide">
            <font-awesome-icon :icon="faArrowUp" role="button" class="mr-1" />
            <b> Offer Other Side </b>
          </button>
        </div>
      </div>
    </div>
    <create-offer-modal
      v-if="createOfferMode"
      :full-game="fullGame"
      :session="session"
      :bid-ladder="bidLadder"
      :ask-ladder="askLadder"
      :spread-number="session.spread"
      :total-number="session.total"
      @return-to-parent-modal="returnToEditOrder"
      @close-modal="closeModal"
      @toggle-loading="toggleLoading"
    />
  </modal>
</template>

<script setup>
import {
  ref,
  computed,
  onMounted,
  onBeforeUnmount,
  capitalize,
  toRaw,
  watch,
} from 'vue';
import CreateOfferModal from './CreateOfferModal.vue';
import InformationalTooltip from '@/components/global/tooltips/InformationalTooltip.vue';
import BidAskLadder from '../BidAskLadder.vue';
import AlertNotification from '@/components/betting/BetSlipAlert.vue';
import OddsSelector from '@/components/betting/OddsSelector.vue';
import Modal from '@/components/global/Modal.vue';
import Spinner from '@/components/global/Spinner.vue';
import dayjs from 'dayjs';
import { useUserStore } from '@/stores/user';
import { useExchangeStore } from '@/stores/exchange';
import { usePlaceTrackerStore } from '@/stores/placeTracker';
import { useAPI, generateIdempotencyKey } from '@/composables/useAPI';
import {
  getOrderSideString,
  getOtherSideType,
  calculateWin,
  roundToFixed2,
  getSideString,
  getOtherSideString,
  convertDecimalToAmerican,
  convertAmericanToDecimal,
  calculateRisk,
  getAnchoredOfferSide,
  round1,
  currentDevice,
  convertAmericanToImpliedProbability,
  convertImpliedProbabilityToAmerican,
  formatEventNameForDisplay,
} from '@/core/utils';
import { getAmericanTickInfo } from '@/core/ticks';
import PostHogUtils from '@/core/PostHogUtils';
import {
  formatBidAndAsk,
  splitOwnAggregateOrder,
  getSideStringForModals,
  getBestOddsWithNumber,
} from '@/core/modalUtils';
import { calculateWinForNoVig } from '@/core/commissionUtils';
import { formatOddsNoCommission } from '@/core/oddsFormatUtils';
import {
  riskWinOptions,
  expiryItems,
  alwaysAllowArbTakes,
  ARB_OFFER_ENABLED,
} from '@/consts/constants';
import { emitter } from '@/core/emitter';
import {
  submitEditOrderSuccessNotification,
  betTakenNotification,
  submitEditOrderFailedNotification,
  cancelOfferNotification,
  cancelOfferFailedNotification,
} from '@/core/notifications';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { faExclamationTriangle } from '@fortawesome/free-solid-svg-icons/faExclamationTriangle';
import { faBolt } from '@fortawesome/free-solid-svg-icons/faBolt';
import { faAngleUp } from '@fortawesome/free-solid-svg-icons/faAngleUp';
import { faAngleDown } from '@fortawesome/free-solid-svg-icons/faAngleDown';
import { faPen } from '@fortawesome/free-solid-svg-icons/faPen';
import { faArrowUp } from '@fortawesome/free-solid-svg-icons/faArrowUp';
import { faArrowDown } from '@fortawesome/free-solid-svg-icons/faArrowDown';
import { faCircleExclamation } from '@fortawesome/pro-solid-svg-icons/faCircleExclamation';
import { faTrashAlt } from '@fortawesome/pro-solid-svg-icons/faTrashAlt';
import { storeToRefs } from 'pinia';

const props = defineProps({
  session: {
    type: Object,
    required: false,
    default: () => {},
  },
  fullGame: {
    type: Object,
    required: false,
    default: () => {},
  },
});

const emit = defineEmits(['closeModal']);

const userStore = useUserStore();
const exchangeStore = useExchangeStore();
const placeTracker = usePlaceTrackerStore();
const { oddsComputed } = storeToRefs(exchangeStore);

const { request: editOrderRequest } = useAPI('session/editOrder/');
const { request: cancelOrderRequest } = useAPI('session/cancel/');

const createOfferMode = ref(false);
const loading = ref(false);
// One Idempotency-Key per operation intent, rotated after each completed
// cycle. Edit and cancel keep separate keys — reusing one key with a
// different body is rejected by the middleware.
let editIntentKey = generateIdempotencyKey();
let cancelIntentKey = generateIdempotencyKey();

/**
 * Functionality for helpful computed values
 */
const betType = ref(props.session.type);
const sideType = computed(() => {
  if (props.session?.type === 'total') {
    return props.session.OU;
  } else if (
    props.session?.type === 'spread' ||
    props.session?.type === 'moneyline'
  ) {
    const participantI = props.fullGame.participants.findIndex(
      (el) => el.id === props.session.participantID,
    );
    const home = props.fullGame.participants[participantI].homeAway === 'home';
    if (props.session?.type === 'spread') {
      return home ? 'homeSpreads' : 'awaySpreads';
    }
    return home ? 'homeMoneylines' : 'awayMoneylines';
  } else if (props.session?.type === 'moneyline1x2') {
    if (props.session.market !== 'draw') {
      const participantI = props.fullGame.participants.findIndex(
        (el) => el.id === props.session.market,
      );
      const home =
        props.fullGame.participants[participantI].homeAway === 'home';
      if (home) {
        return { homeAwayDraw: 'homeMoneylines1x2', side: props.session.side };
      } else {
        return { homeAwayDraw: 'awayMoneylines1x2', side: props.session.side };
      }
    } else {
      return { homeAwayDraw: 'draw1x2', side: props.session.side };
    }
  }
});
const available = computed(() => {
  return (
    parseFloat(userStore.user.displayBalance) +
    parseFloat(userStore.user.liability) -
    parseFloat(userStore.user.creditLimit)
  );
});
const side = computed(() => {
  return {
    type: props.session.type,
    spread: props.session.spread,
    total: props.session.total,
    OU: props.session.OU,
    participantID:
      betType.value !== 'total' ? sessionOthersSee.value.participantID : null,
  };
});

const editOrderButtonString = computed(() => {
  return `${formatOddsNoCommission(-orderOdds.value)} | ${roundToFixed2(orderRisk.value)}`;
});

/**
 * Functionality related to the odds
 */
const orderOdds = ref(100);
const oddsHaveBeenChanged = ref(false);

function updateOdds(value) {
  let newOdds = value;
  const odds = parseFloat(newOdds);
  const prevOdds = orderOdds.value;
  orderOdds.value = odds;
  handleOddsUpdate(odds, prevOdds);
}

function initializeOrderOdds() {
  orderOdds.value = props.session.odds === 100 ? 100 : -1 * props.session.odds;
}

/**
 * Functionality for handling risk win updates
 */
const initialOrderRisk = (
  props.session.bet * (1 - props.session.takenRatio)
).toFixed(2);
const orderRisk = ref(initialOrderRisk);
const orderWin = ref(
  calculateWin(orderRisk.value, -props.session.odds).toFixed(2),
);

function handleOrderRiskUpdate(newRisk) {
  if (!newRisk) {
    orderRisk.value = '';
    orderWin.value = '';
    return;
  }
  orderRisk.value = newRisk;
  const floatRisk = parseFloat(newRisk);
  orderWin.value = calculateWin(floatRisk, orderOdds.value).toFixed(2);
}

function handleOrderWinUpdate(newWin) {
  if (!newWin) {
    orderWin.value = '';
    orderRisk.value = '';
    return;
  }
  orderWin.value = newWin;
  const floatWin = parseFloat(newWin);
  orderRisk.value = calculateRisk(floatWin, orderOdds.value).toFixed(2);
}

function handleOrderRiskDropdown(item) {
  handleOrderRiskUpdate(item);
}

function handleOrderWinDropdown(item) {
  handleOrderWinUpdate(item);
}

function handleSetRiskToMaxAvailable() {
  handleOrderRiskUpdate(available.value);
}

const updatingMaxRisk = ref(false);

// PRO-94: Detect when user exceeds balance/maxLiability but can still edit to max
const exceedsBalanceButCanEditMax = computed(() => {
  if (orderRisk.value === '' || parseFloat(orderRisk.value) < 1) return false;
  if (!validateZero.value) return false;
  if (available.value < 1) return false;

  const riskNum = parseFloat(orderRisk.value);
  const overBalance = riskNum > available.value;
  const overMaxLiability = riskNum > (parseFloat(userStore.user.maxLiability) || Infinity);

  return overBalance || overMaxLiability;
});

// PRO-94: Calculate max risk for edit (no commission for offers)
const maxRiskForEdit = computed(() => {
  const maxLiab = parseFloat(userStore.user.maxLiability) || Infinity;
  return Math.min(Math.floor(available.value), maxLiab);
});

// PRO-94: Calculate win for the max risk amount
const maxWinForEdit = computed(() => {
  if (maxRiskForEdit.value <= 0) return 0;
  return calculateWin(maxRiskForEdit.value, orderOdds.value);
});

async function updateMaxRisk(newMax) {
  if (updatingMaxRisk.value) return;
  updatingMaxRisk.value = true;
  try {
    const { request } = useAPI('/user/updateMaxLiability');
    const response = await request({ maxLiability: newMax });
    if (response.success) {
      userStore.user.maxLiability = newMax;
    }
  } catch (err) {
    console.error('Failed to update max risk:', err);
  } finally {
    updatingMaxRisk.value = false;
  }
}

// PRO-94: Edit order at max available amount
function editOrderAtMax() {
  if (loading.value) {
    // Guard fired = a duplicate tap actually reached the handler. Decision:
    // is the double-submit guard doing real work on real users, or dead
    // weight (over-firing would silently eat legit edits)?
    PostHogUtils.capture('duplicate_submit_blocked', { surface: 'edit' });
    return;
  }
  orderRisk.value = maxRiskForEdit.value.toFixed(2);
  orderWin.value = maxWinForEdit.value.toFixed(2);
  submitEditOrder();
}

// Re-anchor risk/win per the user's create-offer unit mode when repricing an
// existing order — same rule as CreateOfferModal.handleOddsUpdate, minus the
// ladder case (this modal's ladder is display-only).
function handleOddsUpdate(newOdds, prevOdds = newOdds) {
  const mode = userStore.user.createOfferUnitMode || 'riskAUnit';
  const anchor = getAnchoredOfferSide(mode, newOdds);
  const carryFrom = getAnchoredOfferSide(mode, prevOdds);
  const amount = parseFloat(
    carryFrom === 'win' ? orderWin.value : orderRisk.value,
  );
  if (!amount) {
    orderWin.value = '';
    return;
  }
  if (anchor === 'win') {
    orderWin.value = amount.toFixed(2);
    orderRisk.value = calculateRisk(amount, newOdds).toFixed(2);
  } else {
    orderRisk.value = amount.toFixed(2);
    orderWin.value = calculateWin(amount, newOdds).toFixed(2);
  }
}

/**
 * Functionality related to expiry
 */
// Minutes from now until the order's absolute expiry.
function minutesUntilExpiry(session) {
  const dateDifference = Math.abs(new Date(session.expiry) - new Date());
  return Math.floor(dateDifference / 1000 / 60);
}
const initialExpiry = ref(minutesUntilExpiry(props.session));
const expiry = ref(
  props.session.gameStartExpiry ? null : minutesUntilExpiry(props.session),
);

// An own order on the aggregate price feed opens from the in-memory record,
// which has no expiry; the store fetches the real order in the background and
// swaps it into `session` (see exchange store hydrateEditSession). Until then
// the expiry shown is a placeholder, and since every edit sends an expiry the
// order must not be submitted. Everything else on the modal is usable.
const expiryPending = computed(() => props.session.expiryPending === true);

watch(expiryPending, (pending, wasPending) => {
  if (!wasPending || pending) return;
  // The real order arrived; the expiry control was disabled meanwhile, so
  // there is no user input to preserve.
  initialExpiry.value = minutesUntilExpiry(props.session);
  expiry.value = props.session.gameStartExpiry
    ? null
    : minutesUntilExpiry(props.session);
});

// The order turned out to be gone, or the in-memory record was stale: same
// outcome as the blocking path failing to load it.
watch(
  () => props.session.editUnavailable === true,
  (unavailable) => {
    if (!unavailable) return;
    emitter.emit('showAlert', {
      type: 'edit-order',
      shown: true,
      status: 'danger',
      title: 'Unable to Load Order. Please Try Again',
      detail: '',
    });
    closeModal();
  },
);
const endExpiry = ref(
  Math.floor(Math.abs(new Date(props.fullGame.start) - new Date()) / 1000) / 60,
);

function handleOrderExpiryUpdate(newValue) {
  if (Number(newValue) === 0) {
    expiry.value = null;
  } else {
    expiry.value = Number(newValue);
  }
}

function handleOrderExpiryDropdown(item) {
  expiry.value = item.value;
}
/***
 *  Functionality for other side best session (Create Offer)
 */
const otherSideBestSession = ref({});

function initilizeOtherSideBestSession() {
  const otherSideType = getOtherSideType(sideType.value);
  let sourcePath = null; // Initialize to null for safety

  // Helper function for deep cloning
  const deepClone = (obj) => {
    if (!obj) return obj;
    return structuredClone(toRaw(obj));
  };

  if (
    props.session.type === 'moneyline1x2' &&
    props.session.market !== 'draw'
  ) {
    const key = `${otherSideType.homeAwayDraw?.replace('Moneylines1x2', '')}${otherSideType.side}`;
    sourcePath = props.fullGame[otherSideType.homeAwayDraw]?.[key]?.[0];
  } else if (
    props.session.type === 'moneyline1x2' &&
    props.session.market === 'draw'
  ) {
    const key = `${otherSideType.homeAwayDraw?.replace('1x2', '')}${otherSideType.side}`;
    sourcePath = props.fullGame[otherSideType.homeAwayDraw]?.[key]?.[0];
  } else {
    switch (props.session.type) {
      case 'moneyline':
        if (props.fullGame[otherSideType]?.length > 0) {
          sourcePath = props.fullGame[otherSideType][0];
        }
        break;
      case 'spread':
        if (
          props.fullGame[otherSideType]?.[-props.session.spread]?.length > 0
        ) {
          sourcePath = props.fullGame[otherSideType][-props.session.spread][0];
        }
        break;
      case 'total':
        if (props.fullGame[otherSideType]?.[props.session.total]?.length > 0) {
          sourcePath = props.fullGame[otherSideType][props.session.total][0];
        }
        break;
      default:
        sourcePath = null; // Handle unknown types
    }
  }

  if (sourcePath) {
    otherSideBestSession.value = deepClone(sourcePath);
  } else {
    calcOtherSideBestSession();
  }
}

function calcOtherSideBestSession() {
  if (props.session.type !== 'total') {
    const constructedSession = {
      participantID: props.fullGame.participants.find(
        (p) => p !== props.session.participantID,
      ).id,
      odds: null,
      type: props.session.type,
      spread:
        props.session.type === 'spread' ? -1 * props.session.spread : null,
    };
    otherSideBestSession.value = constructedSession;
    if (
      !otherSideBestSession.value ||
      Array.isArray(otherSideBestSession.value)
    )
      otherSideBestSession.value = constructedSession;
  } else {
    const constructedSession = {
      odds: null,
      type: props.session.type,
      OU: ['over', 'under'].find((x) => x !== props.session.OU),
      total: props.session.total,
    };
    otherSideBestSession.value = constructedSession;
  }
}
/***
 * Functionality for the session others see to display in bid ask ladder (Create Offer)
 */
const sessionOthersSee = computed(() => {
  if (!orderOdds.value) return {};
  const pID = otherSideBestSession.value.participantID;
  let americanOdds;
  if (userStore.user.oddsFormat === 'decimal') {
    americanOdds = convertDecimalToAmerican(oddsComputed.value);
  } else if (userStore.user.oddsFormat === 'impliedProbability') {
    americanOdds = convertImpliedProbabilityToAmerican(oddsComputed.value);
  } else {
    americanOdds = oddsComputed.value;
  }
  const formattedOdds = americanOdds === 100 ? 100 : americanOdds * -1;
  const volume =
    orderRisk.value > 0
      ? calculateWinForNoVig(
          orderRisk.value,
          orderOdds.value,
          props.fullGame.league.toLowerCase(),
          props.session.type,
        )
      : 0;
  return {
    isUsers: true,
    // The repriced preview of the order being edited; see CreateOfferModal.
    isProspective: true,
    type: betType.value,
    OU: otherSideBestSession.value.OU,
    isUsers: true,
    createdBy: userStore.user.id,
    name: getSideString(props.fullGame, props.session, true),
    odds: formattedOdds,
    participantID: pID,
    spread: otherSideBestSession.value.spread,
    vol: isNaN(Number(orderWin.value)) ? 0 : Number(orderWin.value),
  };
});

/**
 * Functionality for calculating bid ask ladder (Take Bet)
 */
const bidLadder = computed(() => {
  const sideKey = sideType.value.side === 'yes' ? 'Yes' : 'No';
  let bids;

  // Guard every bucket lookup: when this modal is opened from the open-offers
  // list, fullGame comes from the REST payload and carries no book ladders;
  // JSON.stringify(undefined) would make JSON.parse throw.
  switch (side.value.type) {
    case 'moneyline':
      bids = JSON.parse(JSON.stringify(props.fullGame[sideType.value] ?? []));
      break;
    case 'spread':
      bids = JSON.parse(
        JSON.stringify(
          props.fullGame[sideType.value]?.[props.session.spread] ?? [],
        ),
      );
      break;
    case 'total':
      bids = JSON.parse(
        JSON.stringify(
          props.fullGame[sideType.value]?.[props.session.total] ?? [],
        ),
      );
      break;
    case 'moneyline1x2':
      if (props.session.market === 'draw') {
        bids = JSON.parse(
          JSON.stringify(props.fullGame['draw1x2']?.[`draw${sideKey}`] ?? []),
        );
      } else {
        const homeAwayKey = sideType.value.homeAwayDraw;
        bids = JSON.parse(
          JSON.stringify(
            props.fullGame[homeAwayKey]?.[
              `${homeAwayKey.replace('Moneylines1x2', '')}${sideKey}`
            ] ?? [],
          ),
        );
      }
      break;
    default:
      bids = [];
  }

  if (bids) {
    // Aggregate rows carry the first stamped order id, so also match via
    // userOrderIDs in case a different one of the user's orders was resolved.
    const bidToUpdate = bids.find(
      (bid) =>
        bid.id === props.session.id ||
        bid.userOrderIDs?.includes(String(props.session.id)),
    );
    if (bidToUpdate) {
      const newOdds = orderOdds.value === 100 ? 100 : -orderOdds.value;
      if (bidToUpdate.priceFeedAggregate && bidToUpdate.createdBy) {
        // The aggregate row is the WHOLE price level (user's + others'
        // volume). Repricing it in place would show other users' takeable
        // volume at the edited odds; split it so only the user's share
        // moves and the others' share stays at the real book price.
        const { others, own } = splitOwnAggregateOrder(bidToUpdate);
        const replacement = [];
        if (others) replacement.push(others);
        if (own) replacement.push({ ...own, odds: newOdds });
        bids.splice(bids.indexOf(bidToUpdate), 1, ...replacement);
      } else {
        bidToUpdate.odds = newOdds;
      }
    }
  }

  return formatBidAndAsk(
    bids || [],
    props.fullGame.league.toLowerCase(),
    props.session.type,
  );
});

const askLadder = computed(() => {
  const sideKey = sideType.value.side === 'yes' ? 'No' : 'Yes';
  const otherSide = getOtherSideType(sideType.value);
  let asks;

  switch (side.value.type) {
    case 'moneyline':
      asks = props.fullGame[otherSide];
      break;
    case 'spread':
      asks = props.fullGame[otherSide]?.[-props.session.spread];
      break;
    case 'total':
      asks = props.fullGame[otherSide]?.[props.session.total];
      break;
    case 'moneyline1x2':
      if (props.session.market === 'draw') {
        asks = props.fullGame['draw1x2']?.[`draw${sideKey}`];
      } else {
        const homeAwayKey = sideType.value.homeAwayDraw;
        asks =
          props.fullGame[homeAwayKey]?.[
            `${homeAwayKey.replace('Moneylines1x2', '')}${sideKey}`
          ];
      }
      break;
    default:
      asks = null; // or handle default case
  }

  return formatBidAndAsk(
    asks,
    props.fullGame.league.toLowerCase(),
    props.session.type,
  );
});

/**
 * Arb Offer state — lets the user re-flag an existing order so the edited
 * price rests even if it would cross the opposing side of the book.
 */
const arbOfferEnabled = ref(
  (() => {
    if (props.session?.arbOffer) return true;
    if (userStore.user?.defaultArbOffer) return true;
    try {
      if (localStorage.getItem('defaultArbOffer') === 'true') return true;
    } catch (err) {
      // ignore — fall through to other defaults
    }
    if (
      userStore.user?.viewOddsWithCommission &&
      !alwaysAllowArbTakes.includes(userStore.user?.username) &&
      !userStore.user?.isPro
    ) {
      return true;
    }
    return false;
  })(),
);

const isInArb = computed(() => {
  return (
    (userStore.user.oddsFormat === 'decimal' &&
      bidLadder.value.length > 0 &&
      askLadder.value.length > 0 &&
      oddsComputed.value <=
        convertAmericanToDecimal(otherSideBestSession.value?.odds)) ||
    (userStore.user.oddsFormat === 'impliedProbability' &&
      bidLadder.value.length > 0 &&
      askLadder.value.length > 0 &&
      oddsComputed.value >=
        convertAmericanToImpliedProbability(
          otherSideBestSession.value?.odds,
        )) ||
    (userStore.user.oddsFormat === 'american' &&
      Math.abs(oddsComputed.value / 100) >= 1 &&
      bidLadder.value.length > 0 &&
      askLadder.value.length > 0 &&
      orderOdds.value <= otherSideBestSession.value?.odds &&
      oddsComputed.value.toString().length >= 3 &&
      orderOdds.value.toString().length >= 3)
  );
});

const dontAllowArbs = computed(() => {
  if (
    userStore.user.viewOddsWithCommission &&
    !alwaysAllowArbTakes.includes(userStore.user.username) &&
    !userStore.user.isPro
  ) {
    return true;
  }
  return false;
});

const canOfferAsArb = computed(
  () => ARB_OFFER_ENABLED && isInArb.value,
);

const arbOrderMessage = computed(() => {
  if (dontAllowArbs.value) {
    return 'Edit is in arbitrage and will not be placed unless Arb Offer is on.';
  }
  return 'This order will be filled immediately, please check this offer!';
});

function openArbOfferInfo() {
  emitter.emit('openArbOfferInfoModal');
}

function toggleArbOffer() {
  arbOfferEnabled.value = !arbOfferEnabled.value;
}

/**
 * Functionality that is HTML and CSS Related (Take Bet) (Create Offer)
 */

const editOrderButtonDisabled = computed(() => {
  if (
    oddsHaveBeenChanged.value &&
    orderRisk.value === initialOrderRisk &&
    initialExpiry.value === Number(expiry.value)
  ) {
    return true;
  }
  return false;
});

const onFocusValues = ref({
  risk: false,
  win: false,
  createOfferRisk: false,
  createOfferWin: false,
  odds: false,
  expiry: false,
  keyboard: false,
  max: true,
});

const dropdownRisk = ref(false);
const dropdownWin = ref(false);
const dropdownExpiry = ref(false);

onMounted(() => {
  window.addEventListener('click', dropdownListener);
  // Denominator for the market-making rate: the Edit Order modal always shows
  // the "Offer Other Side" button, so every open is a chance to MM. Pair with
  // offer_other_side_clicked (the click) to get "when users can quote the other
  // side, how often do they".
  PostHogUtils.capture('edit_order_opened', {
    is_live: !!props.fullGame?.live,
    market_type: props.fullGame?.league?.includes('PROPS')
      ? 'prop'
      : props.session?.type,
  });
});

onBeforeUnmount(() => {
  window.removeEventListener('click', dropdownListener);
});

/**
 * Functionality Misc
 */
const sendMessage = ref(false);
function openDropdown() {}

const validateZero = computed(() => {
  const win = orderWin.value;
  const risk = orderRisk.value;

  if (risk[0] === '0' || win[0] === '0') {
    return false;
  }
  return true;
});

/**
 * Functionality to toggle to create new offer mode
 */

function handleOfferOtherSide() {
  PostHogUtils.capture('offer_other_side_clicked', {
    is_live: !!props.fullGame?.live,
    market_type: props.fullGame?.league?.includes('PROPS')
      ? 'prop'
      : props.session?.type,
  });
  createOfferMode.value = true;
}

function returnToEditOrder() {
  createOfferMode.value = false;
}

function toggleLoading(value) {
  loading.value = value;
}

/**
 * Functionality to submit edit order and cancel order
 */

async function submitEditOrder() {
  // The real expiry has not loaded yet (see expiryPending); the button is
  // disabled too, this covers a tap queued before the next render. Also
  // covers editOrderAtMax, which submits through here.
  if (expiryPending.value) return;
  // Synchronous re-entrancy guard — :disabled only applies on the next
  // render, so queued taps would otherwise each submit.
  if (loading.value) {
    // See editOrderAtMax — is the guard firing on real users?
    PostHogUtils.capture('duplicate_submit_blocked', { surface: 'edit' });
    return;
  }
  // Declared outside try so finally reports the terminal status exactly once.
  let placeAttemptId = null;
  let httpStatus = 'rejected';
  // Distinguishes "server answered but created nothing" (rejected) from "the
  // request itself threw" (error) — the failed-cancel path below throws, but
  // it is a server response, not a transport failure.
  let serverResponded = false;
  // Edit response leg for exact order-ID side-update matching; same
  // { matched, unmatched } shape the place endpoint returns.
  let editSessionsForTelemetry = null;
  try {
    loading.value = true;
    const session = {
      type: betType.value,
      participantID:
        betType.value !== 'total' ? side.value.participantID : null,
      spread: betType.value === 'spread' ? props.session.spread : null,
      OU: betType.value === 'total' ? sessionOthersSee.value.OU : null,
      total: props.session.total,
      market: props.session.market,
      side: props.session.side,
    };
    let sideString;
    if (
      userStore.user.displayRotationNumbers &&
      betType.value !== 'moneyline1x2'
    ) {
      sideString =
        session.type !== 'total'
          ? `${props.fullGame.participants.find((p) => p.id === session.participantID).rotationNumber} ${getSideString(props.fullGame, session)}`
          : props.fullGame.participants
            ? `${props.fullGame.participants.find((p) => p.homeAway === 'away').rotationNumber} ${getSideString(props.fullGame, session)}`
            : `${getSideString(props.fullGame, session)}`;
    } else {
      sideString = getSideString(props.fullGame, session);
    }
    const editPayload = {
      sessionID: props.session.id,
      orderOdds: getAmericanTickInfo(orderOdds.value).snap,
      orderVolume: Number(orderRisk.value),
      intendedOrderVolume: Number(initialOrderRisk),
      expiryChange: Math.floor(Number(expiry.value)),
      sendMessage: sendMessage.value,
      market: props.session.market,
      side: props.session.side,
      type: betType.value,
      // Always send an explicit orderType so the backend can tell when a user
      // is demoting a postArb order back to a regular limit/post order. The
      // edit endpoint forwards orderType straight through to
      // place.HandlePlaceRequest; omitting it would leave the original
      // postArb type in place. Matches CreateOfferModal.
      orderType: (() => {
        if (
          ARB_OFFER_ENABLED &&
          arbOfferEnabled.value &&
          canOfferAsArb.value
        ) {
          return 'postArb';
        }
        return dontAllowArbs.value && !userStore.user.isPro ? 'post' : 'limit';
      })(),
    };
    // Open the telemetry attempt right before the request; finally reports the
    // HTTP result. An edit is cancel + repost, so the confirming side update
    // carries the NEW session id — the post-shaped, exact case.
    placeAttemptId = placeTracker.register({
      operation: 'edit',
      orderType: editPayload.orderType,
      gameId: props.fullGame?.id,
      marketType: props.fullGame?.league?.includes('PROPS')
        ? 'prop'
        : props.session?.type,
      isLive: !!props.fullGame?.live,
      leagueId: props.fullGame?.league ?? null,
      sportId: props.fullGame?.sport ?? null,
    });

    const response = await editOrderRequest(editPayload, null, {
      idempotencyKey: editIntentKey,
    });
    serverResponded = true;
    if (!response.cancelled?.success) {
      // Server answered and nothing was created — no side update is expected,
      // so this must not land in the drain-rate numerator.
      throw new Error('Please refresh and try again');
    }
    httpStatus = 'ok';
    // The edit endpoint returns { cancelled, matched, unmatched } at the TOP
    // level — there is no createdSessions wrapper like /session/v2/place has.
    // Reshape into the { matched, unmatched } leg the tracker expects, or it
    // finds no target order ids and books a successful edit as 'rejected'.
    editSessionsForTelemetry = {
      matched: response.matched ?? [],
      unmatched: response.unmatched ?? {},
    };

    const notifPackage = {
      sideString,
      amount: orderRisk.value,
      odds: formatOddsNoCommission(getAmericanTickInfo(orderOdds.value).snap),
    };

    submitEditOrderSuccessNotification(notifPackage);
    if (
      response.createdSessions &&
      response.createdSessions.matched.length > 0
    ) {
      for (let i = 0; i < response.createdSessions.matched.length; i++) {
        betTakenNotification(
          {
            sideString: getSideString(props.fullGame, session),
            amount: response.createdSessions.matched[i].risk,
            odds: formatOddsNoCommission(
              response.createdSessions.matched[i].odds,
            ),
          },
          currentDevice(),
        );
      }
    }
  } catch (error) {
    // Latch: once the edit came back ok, a throw from the notification code
    // below it is a UI bug, not a failed edit. A server response that created
    // nothing stays 'rejected'; only a genuine transport/throw is 'error'.
    if (httpStatus !== 'ok') {
      httpStatus = serverResponded ? 'rejected' : 'error';
    }
    console.error('error updating offer ');
    let data = {
      detail: error,
    };
    if (Array.isArray(error) && error.includes('locked')) {
      data = {
        title: 'Account Paused',
        detail: 'Please contact an admin to unlock your account.',
      };
    }
    submitEditOrderFailedNotification(data);
  } finally {
    closeModal();
    loading.value = false;
    // Guard: the re-entrancy return above register() leaves placeAttemptId null.
    if (placeAttemptId) {
      placeTracker.recordHttpResponse(
        placeAttemptId,
        httpStatus,
        editSessionsForTelemetry,
      );
    }
    editIntentKey = generateIdempotencyKey();
  }
}

async function cancelOrder() {
  if (loading.value) {
    // See editOrderAtMax — is the guard firing on real users?
    PostHogUtils.capture('duplicate_submit_blocked', { surface: 'cancel' });
    return;
  }
  try {
    loading.value = true;
    const response = await cancelOrderRequest(
      {
        sessionID: props.session.id,
      },
      null,
      { idempotencyKey: cancelIntentKey },
    );
    const { description, remaining, odds } = response;
    cancelOfferNotification(
      description,
      remaining,
      formatOddsNoCommission(odds),
    );
  } catch (error) {
    console.log('error cancelling order ');
    cancelOfferFailedNotification();
  } finally {
    closeModal();
    loading.value = false;
    cancelIntentKey = generateIdempotencyKey();
  }
}

function closeModal() {
  emit('closeModal');
}

/**
 * Functionality to set up / tear down edit order modal
 */

function initializeEditOrderModal() {
  initilizeOtherSideBestSession();
  initializeOrderOdds();
}

initializeEditOrderModal();

//Function to close dropdowns when clciked outside
function dropdownListener(e = {}) {
  if (
    dropdownRisk.value &&
    (!e.target ||
      (e.target.className !== 'input-group-div' &&
        e.target.dataset.icon !== 'angle-up'))
  ) {
    dropdownRisk.value = false;
  }
  if (
    dropdownWin.value &&
    (!e.target ||
      (e.target.className !== 'input-group-div' &&
        e.target.dataset.icon !== 'angle-up'))
  ) {
    dropdownWin.value = false;
  }
  if (
    dropdownExpiry.value &&
    (!e.target ||
      (e.target.className !== 'input-group-div' &&
        e.target.dataset.icon !== 'angle-up'))
  ) {
    dropdownExpiry.value = false;
  }
}
</script>

<style scoped lang="scss">
@use 'sass:map';
@use '@/scss/main.scss';

$blue1: #dfe1ef;
$blue2: #343b81;
$border-radius-button: 35px;

.difference-best {
  color: main.$color-primary;
}

.difference-not-best {
  color: main.$red-301;
}

.pinny {
  color: main.$orange-100;

  @media screen and (max-width: map.get(main.$grid-breakpoints, sm)) {
    font-size: 0.75rem;
  }
}

.cris {
  color: main.$blue-600;

  @media screen and (max-width: map.get(main.$grid-breakpoints, sm)) {
    font-size: 0.75rem;
  }
}

.othersee {
  color: main.$blue-1100;

  @media screen and (max-width: map.get(main.$grid-breakpoints, sm)) {
    font-size: 0.75rem;
  }
}

.four {
  @media screen and (max-width: map.get(main.$grid-breakpoints, sm)) {
    font-size: 0.9rem;
  }
}

.comparison {
  color: main.$black;

  @media screen and (max-width: map.get(main.$grid-breakpoints, sm)) {
    font-size: 0.65rem;
  }
}

.create-offer-btn {
  width: 100%;
  height: 100%;
  font-size: 1rem;
  padding: 0.875rem 1rem;
  background-color: main.$grey-1100_01;
  color: main.$blue-800_01;
  border-radius: $border-radius-button;

  //&:hover {
  //  background: saturate(main.$blue1, 10%);
  //}

  &:disabled {
    background-color: main.$grey-1800;
    color: main.$white;
  }
}

//.create-offer-btn {
//  width: 100%;
//  height: 100%;
//  font-size: 1rem;
//  padding: .875rem 1rem;
//  background-color: #343B81;
//  color: #FFFFFF;
//  border-radius: $border-radius-button;

//  //  &:hover {
//    background: saturate(main.$background-color-ask, 20%);
//  }

//  &:disabled {
//    background-color: #cccccc;
//    color: white;
//  }
//}

.contest-notification {
  position: relative;
  background: main.$orange-100;
  text-align: center;
  border-radius: 6px;
  color: main.$white;
  font-weight: main.$font-weight-semibold;
  font-size: main.$font-size-sm;
  text-shadow: 1px 2px 5px 0 main.$black-201;
  transition: background 0.2s ease;
  display: block;
  text-decoration: none;

  @media screen and (max-width: map.get(main.$grid-breakpoints, md)) {
    display: flex;
    justify-content: space-around;
    align-items: center;
  }
}

.create-other-offer-btn {
  // toggleable col-8
  width: 100%;
  font-size: main.$font-size-sm;
  padding: 0.875rem 2rem;
  background-color: main.$blue-1100;

  &:disabled {
    background-color: main.$grey-1800;
    color: main.$white;
  }
}

h1 {
  font-size: 1.5rem;
  color: main.$color-primary_01;
  font-weight: bold;

  @media screen and (max-width: map.get(main.$grid-breakpoints, sm)) {
    font-size: 1.25rem;
  }
}

h5 {
  font-size: 0.8rem;

  @media screen and (max-width: map.get(main.$grid-breakpoints, sm)) {
    font-size: 0.7rem;
  }

  &.header-info {
    color: main.$black-203;
  }
}

button {
  font-size: 0.875rem;
  width: 100%;
  padding: 0.85rem;
  background-color: main.$color-primary;
}

.edit-order-btn {
  width: 100%;
  height: 100%;
  font-size: 1rem;
  padding: 0.875rem 1rem;
  background-color: main.$blue-800_02;
  color: main.$grey-1100_02;
  border-radius: $border-radius-button;

  &:disabled {
    background-color: main.$grey-1800;
    color: main.$white;
  }

  &.btn-max-balance {
    background-color: main.$orange-100;
    color: main.$white;

    &:hover {
      filter: brightness(0.95);
    }
  }
}

.time {
  font-size: main.$font-size-sm;
  font-weight: main.$font-weight-semibold;
}

.risk-win-items {
  justify-content: space-between;
}

input[type='text'] {
  flex-grow: 1;
  min-width: 0;
  font-size: 16px;
  font-weight: bold;
}

.bet-close-btn {
  cursor: pointer;
  color: main.$red-301;

  &:hover {
    color: main.$red-101;
  }
}

.tick {
  background-color: main.$grey-500;
  user-select: none;
}

.close {
  position: absolute;
  right: 3rem;
  top: 0;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  line-height: 0;
  z-index: 3;
  transition: transform ease 0.2s;

  &:hover {
    transform: scale(1.2);
  }

  @media screen and (max-width: map.get(main.$grid-breakpoints, md)) {
    position: static;
    display: block;
    padding: 1rem;
    height: auto;
  }
}

.cancel-button {
  width: 100%;
  height: 100%;
  font-size: 1rem;
  padding: 0.875rem 1rem;
  background: main.$red-302;
  border-radius: $border-radius-button;

  &:hover {
    background: main.$red-303;
  }
}

.error {
  color: main.$red-500;
}

.input-group {
  position: relative;
  display: flex;
  flex-wrap: wrap;
  width: 100%;
  outline: 1px solid main.$grey-300;
  box-shadow: 0 2px 10px 0 main.$blue-1200;
  border-radius: 12px;
  padding: 8px 0 8px 5px;
  color: main.$blue-800_01;

  input {
    width: calc(100% - 100px);
    background-color: main.$transparent_01;
    padding: 0 0.5rem;
    font-size: 18px;

    &:focus {
      border-bottom-color: main.$transparent_01;
    }
  }

  .input-group-button {
    width: 50px;

    .available {
      color: main.$white;
      background-color: main.$blue-400;
      padding: 5px;
      font-size: 0.8rem;
      border-radius: 8px;
      float: right;
    }
  }

  .input-group-div {
    width: 100px;
    display: inline;
    padding: 0 0.3rem;
    border-left: 2px solid main.$grey-900;
    align-items: center;
    font-weight: bold;
    cursor: pointer;
    font-size: 16px;
    text-align: center;
  }

  &.input-group-win-risk {
    input {
      width: calc(100% - 100px - 50px);
    }
  }
}

.dropdown-menu {
  background-color: main.$white;
  overflow: hidden;
  z-index: 1;
  width: 80px;
  position: absolute;
  border: 1px solid main.$black-200;
  border-radius: 5px;
  top: -200px;
  right: -5px;

  &.expiry {
    width: 100px;
    top: -120px;

    @media screen and (max-width: map.get(main.$grid-breakpoints, sm)) {
      width: 40%;
    }
  }

  @media screen and (max-width: map.get(main.$grid-breakpoints, sm)) {
    right: 0;
    width: 40%;
  }

  div {
    width: 100%;
    font-size: 16px;
    padding: 0.5rem 1rem;
    clear: both;
    font-weight: 400;
    text-align: inherit;
    white-space: nowrap;
    background-color: main.$transparent_03;
    border-bottom: 1px solid main.$blue-102;
    cursor: pointer;

    &:last-child {
      border-bottom: main.$transparent;
    }

    &:hover {
      background-color: main.$white-101;
      font-weight: bold;
    }
  }
}

.risk-win-items {
  @media screen and (max-width: map.get(main.$grid-breakpoints, sm)) {
    display: contents;

    > div {
      padding: 0 !important;
      margin: 10px 0;
    }
  }
}

.header-info {
  text-align: center;
}

h6 {
  color: main.$blue-1100;
  font-weight: main.$font-weight-light;

  @media screen and (max-width: map.get(main.$grid-breakpoints, sm)) {
    font-size: 0.5rem;
  }
}

.add-balance {
  text-decoration: underline;
  color: main.$red-500;
  font-weight: 700;
  cursor: pointer;
}

.add-available-risk {
  text-decoration: underline;
  color: main.$color-primary;
  font-weight: 700;
  cursor: pointer;
}

.alert-text {
  @media screen and (max-width: map.get(main.$grid-breakpoints, sm)) {
    font-size: 11px;
    font-weight: bold;
  }
}

.propStyle {
  display: -webkit-inline-box;
}

/* ============================================================
   Arb Offer card — matches CreateOfferModal. Stripe + tint
   switch from warning (yellow) to success (green) when the
   toggle is flipped on. Clicking anywhere on the card flips it.
   ============================================================ */
$arb-warn-accent: #c99017;
$arb-warn-tint: #fff6e0;
$arb-success-accent: #2fa36a;
$arb-success-tint: #e9f7ef;

.arb-card {
  margin-top: 0.5rem;
  position: relative;
  overflow: hidden;
  border-radius: 10px;
  background: $arb-warn-tint;
  border: 1px solid rgba(0, 0, 0, 0.04);
  box-shadow: inset 4px 0 0 $arb-warn-accent;
  transition: background 0.2s ease, box-shadow 0.2s ease;
  cursor: pointer;
  user-select: none;

  &:focus-visible {
    outline: 2px solid main.$color-primary_01;
    outline-offset: 2px;
  }

  &.is-on {
    background: $arb-success-tint;
    box-shadow: inset 4px 0 0 $arb-success-accent;
  }

  [theme='night'] & {
    background: rgba(201, 144, 23, 0.16);
    border-color: rgba(255, 255, 255, 0.06);

    &.is-on {
      background: rgba(47, 163, 106, 0.18);
    }
  }
}
.arb-card-message {
  display: flex;
  align-items: flex-start;
  gap: 0.55rem;
  padding: 0.55rem 0.85rem 0.55rem 1rem;
  color: $arb-warn-accent;

  .arb-card.is-on & {
    color: $arb-success-accent;
  }
}
.arb-card-status-icon {
  flex-shrink: 0;
  font-size: 0.9rem;
}
.arb-card-text {
  font-size: 0.9rem;
  line-height: 1.35;
  font-weight: main.$font-weight-semibold;

  @media screen and (max-width: map.get(main.$grid-breakpoints, 'sm')) {
    font-size: 0.8rem;
  }
}
.arb-card-controls {
  display: flex;
  align-items: center;
  gap: 0.55rem;
  padding: 0.55rem 0.85rem 0.55rem 1rem;
  border-top: 1px solid rgba(0, 0, 0, 0.06);
  background: rgba(255, 255, 255, 0.55);

  [theme='night'] & {
    background: rgba(255, 255, 255, 0.04);
    border-top-color: rgba(255, 255, 255, 0.06);
  }
}
.arb-card-title {
  display: flex;
  align-items: center;
  gap: 0.55rem;
  margin: 0;
}
.arb-card-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  background: rgba(111, 117, 177, 0.22);
  color: main.$color-primary_01;
  font-size: 0.72rem;
  flex-shrink: 0;
  transition: background 0.2s ease, color 0.2s ease;

  .arb-card.is-on & {
    background: $arb-success-accent;
    color: main.$white_01;
  }
}
.arb-card-label {
  font-weight: main.$font-weight-semibold;
  font-size: 0.95rem;
  color: main.$blue-1100;
}
.arb-card-learn {
  text-decoration: underline;
  cursor: pointer;
  font-size: 0.82rem;
  color: main.$color-primary_01;
  opacity: 0.75;
  white-space: nowrap;

  &:hover {
    opacity: 1;
  }
}
.arb-card-spacer {
  flex: 1 1 auto;
}
.arb-card-state {
  font-size: 0.78rem;
  font-weight: main.$font-weight-semibold;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  opacity: 0.55;
  color: main.$blue-1100;

  .arb-card.is-on & {
    color: $arb-success-accent;
    opacity: 1;
  }
}
.arb-card .toggle-slider.is-on {
  background: $arb-success-accent;
}
@media screen and (max-width: map.get(main.$grid-breakpoints, sm)) {
  .arb-card-controls {
    flex-wrap: wrap;
    row-gap: 4px;
  }
  .arb-card-learn {
    order: 4;
    flex-basis: 100%;
    text-align: left;
  }
}

.toggle-switch {
  position: relative;
  display: inline-block;
  width: 42px;
  height: 24px;
  flex-shrink: 0;
  pointer-events: none;
}
.toggle-slider {
  position: absolute;
  inset: 0;
  background: #c4c4c4;
  border-radius: 24px;
  transition: background 0.2s ease;

  &.is-on {
    background: main.$color-primary_01;

    &::before {
      transform: translateX(18px);
    }
  }

  &::before {
    content: '';
    position: absolute;
    left: 3px;
    top: 3px;
    width: 18px;
    height: 18px;
    border-radius: 50%;
    background: #fff;
    transition: transform 0.2s ease;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.15);
  }
}
</style>
